import { Flex, HStack, Box, IconButton, Text } from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { parseISO } from 'date-fns';
import { EntityAddress } from '../entity-address';
import {
    assignedUnitToMapLedgerUnit,
    getUnitIconByUniqueValueKey,
    getUnitUniqueValueKey,
} from '../helpers';
import { useEnabledFeatures } from '../../../../common/hooks/enabled-features/use-enabled-features';
import { useEsriMapStore } from '../../../hooks/use-esri-map-store';
import { useEsriMapView } from '../../../hooks/use-esri-map-view';
import { MapAssignedUnitInfo } from '@mark43/cad-api';
import { isValidLatLng } from '../../../../location/helpers';
import { getMapViewportUnitLocationSource } from '../../../../esri/helpers/locations';
import { DELAY_FOR_UNIT_UPDATE } from '../../../configuration';
import { LastUpdateTime } from '../last-update-time';
import { CommonSize, getOneSizeSmaller } from '../../../../common/helpers/utils';

type EventAssignedUnitOverviewProps = {
    unit: MapAssignedUnitInfo;
    size?: CommonSize;
};

export const EventAssignedUnitOverview = observer(function EventAssignedUnitOverview({
    unit,
    size,
}: EventAssignedUnitOverviewProps): React.ReactElement {
    const mapStore = useEsriMapStore();
    const { goTo } = useEsriMapView();
    const { subpremiseSupport } = useEnabledFeatures();
    const smallerSize = getOneSizeSmaller(size);

    const {
        callSign,
        dispatchArea,
        locationSource,
        placeName,
        areCoordinatesVerified,
        displayLineFirst,
        displayLineLast,
        displayLineOptionalMiddle,
        displayLineSubPremise,
        unitStateDisplay,
        unitStatusUpdatedDateUtc,
    } = unit;

    const mapLedgerUnitFullData = assignedUnitToMapLedgerUnit(mapStore, unit);
    const mapViewportUnit = mapStore.getMapViewportUnit(unit.entityId);
    const unitLocationSource = getMapViewportUnitLocationSource(
        mapViewportUnit,
        mapStore.getEnabledFeaturesConfig().showUnitLocationWhenGpsIsNotAvailable
    );
    const isGpsLocation = unitLocationSource === 'GPS';

    const unitLocation = isGpsLocation
        ? mapViewportUnit?.gpsLocation
        : mapViewportUnit?.geoLocation;
    const locationTimestamp = unitLocation?.timestamp;

    const unitUniqueValueKey = getUnitUniqueValueKey({
        selectedMapLedgerUnit: mapLedgerUnitFullData,
        isLocationPotentiallyInaccurate: mapViewportUnit?.isLocationPotentiallyInaccurate,
        isUser: false,
    });
    const unitIcon = getUnitIconByUniqueValueKey(unitUniqueValueKey);

    const latitudeCoordinate = unitLocation?.latitude;
    const longitudeCoordinate = unitLocation?.longitude;

    const unitStatusUpdatedDateParsed = parseISO(unitStatusUpdatedDateUtc);

    const areCoordinatesValid = isValidLatLng(latitudeCoordinate, longitudeCoordinate);

    const handleLocationButtonClick = () => {
        if (areCoordinatesValid && goTo) {
            goTo({ latitude: latitudeCoordinate!, longitude: longitudeCoordinate! });
        }
    };

    return (
        <Flex alignItems="start" gap={2} marginBottom={3} width="100%">
            {unitIcon && (
                <Flex alignItems="center" minWidth={40} w={40}>
                    <img
                        src={unitIcon}
                        alt="icon"
                        data-icon-key={unitUniqueValueKey}
                        data-testid="MAP_EVENT_ASSIGNED_UNIT_ICON"
                    />
                </Flex>
            )}
            <Flex flexDirection="column" width="100%">
                <HStack marginTop={0} marginBottom={3}>
                    <Text fontSize={size} fontWeight="bold" style={{ wordBreak: 'break-word' }}>
                        Unit {callSign}{' '}
                        <Text as="span" fontSize={size}>
                            ({dispatchArea})
                        </Text>
                    </Text>
                    <IconButton
                        aria-label="Location"
                        icon="Crosshairs"
                        variant="ghost"
                        size={size}
                        disabled={!areCoordinatesValid}
                        onClick={handleLocationButtonClick}
                        data-testid="MAP_CROSSHAIRS_BUTTON"
                    />
                </HStack>
                {isGpsLocation ? (
                    <EntityAddress
                        shouldTitleBeDisplayed={false}
                        shouldSearchReverseGeocodeLocation={false}
                        isGpsLocation={true}
                        latitudeCoordinate={latitudeCoordinate}
                        longitudeCoordinate={longitudeCoordinate}
                        locationTimestamp={locationTimestamp}
                        size={smallerSize}
                    />
                ) : (
                    <EntityAddress
                        shouldTitleBeDisplayed={false}
                        shouldSearchReverseGeocodeLocation={false}
                        isGpsLocation={false}
                        placeName={placeName}
                        source={locationSource}
                        areGeoLocationCoordinatesVerified={areCoordinatesVerified}
                        displayLineFirst={displayLineFirst}
                        displayLineLast={displayLineLast}
                        displayLineOptionalMiddle={displayLineOptionalMiddle}
                        displayLineSubPremises={displayLineSubPremise}
                        latitudeCoordinate={latitudeCoordinate}
                        longitudeCoordinate={longitudeCoordinate}
                        subpremiseSupport={subpremiseSupport}
                        locationTimestamp={locationTimestamp}
                        size={smallerSize}
                    />
                )}
                <Box data-testid="MAP_EVENT_ASSIGNED_UNIT_STATUS_INFO">
                    <Text
                        as="span"
                        fontSize={smallerSize}
                        fontWeight="bold"
                        style={{ wordBreak: 'break-word', marginRight: '8px' }}
                    >
                        {unitStateDisplay}
                    </Text>
                    <LastUpdateTime
                        timeUpdateKey={unitStateDisplay}
                        lastTimestamp={unitStatusUpdatedDateParsed}
                        delay={DELAY_FOR_UNIT_UPDATE}
                        size={smallerSize}
                        testid="MAP_EVENT_ASSIGNED_UNIT_TIME_IN_STATUS"
                    />
                </Box>
            </Flex>
        </Flex>
    );
});
